import { CaretUp, CaretDown, ArrowSquareOut } from '@phosphor-icons/react';
import { useFfeTournaments, type FfeTournament } from '@/api/ratings';

/**
 * FfeTournamentsTable — historique des tournois FFE de Léon (Stats Hub V2 PR #I).
 *
 * Consomme `useFfeTournaments()` (officiel `@/api/ratings`) qui frappe `/users/me/ffe/tournaments`
 * (PR #160). Pattern visuel : table responsive, tabular-nums sur les colonnes
 * numériques (DESIGN §3 obligatoire), Phosphor icons pour les flèches Δ.
 *
 * Empty state : carte centrée invitant à jouer le prochain open. Loading state :
 * skeleton 5 rows. Lien tournoi externe FFE (`url`) en `target="_blank"
 * rel="noopener noreferrer"` (sécurité).
 */

const CADENCE_LABEL: Record<string, string> = {
  standard: 'Std',
  rapide: 'Rapide',
  blitz: 'Blitz',
};

function formatCadence(timeControl: string): string {
  return CADENCE_LABEL[timeControl] ?? timeControl;
}

function formatScore(row: FfeTournament): string {
  if (row.score === null || row.rounds_played === null) {
    return '—';
  }
  return `${row.score.toFixed(1)} / ${row.rounds_played}`;
}

function VariationCell({ value }: { value: number | null }): JSX.Element {
  if (value === null) {
    return <span className="text-ink-300 font-mono tabular-nums">—</span>;
  }
  if (value > 0) {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono tabular-nums"
        style={{ color: 'var(--volt-500)' }}
      >
        <CaretUp size={12} weight="bold" aria-hidden="true" />+{value}
      </span>
    );
  }
  if (value < 0) {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono tabular-nums"
        style={{ color: 'var(--coral-500)' }}
      >
        <CaretDown size={12} weight="bold" aria-hidden="true" />
        {value}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 font-mono tabular-nums"
      style={{ color: 'var(--ink-300)' }}
    >
      0
    </span>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <ul className="flex flex-col gap-1.5" aria-label="Chargement des tournois">
      {Array.from({ length: 5 }).map((_, i) => (
        <li
          key={i}
          className="h-9 rounded-md border border-ink-700 bg-ink-900 animate-pulse"
          aria-hidden="true"
        />
      ))}
    </ul>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-6 px-4 rounded-md border border-ink-700 bg-ink-900">
      <p className="text-paper-50 text-body m-0">
        Aucun tournoi FFE détecté pour l'instant.
      </p>
      <p className="text-ink-300 text-body-sm italic mt-1 m-0">
        Joue ton prochain open, on capte automatiquement.
      </p>
    </div>
  );
}

export function FfeTournamentsTable(): JSX.Element {
  const q = useFfeTournaments(undefined, 20);

  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-6 flex flex-col gap-3">
      <div>
        <h3 className="text-heading-3 text-paper-50 m-0">Tournois FFE</h3>
        <p className="text-body-sm text-ink-300 italic mt-1 m-0">
          Importés automatiquement depuis echecs.asso.fr.
        </p>
      </div>

      {q.isLoading && <LoadingSkeleton />}

      {q.isError && (
        <p role="alert" className="text-coral-500 text-body-sm">
          Impossible de charger les tournois FFE.
        </p>
      )}

      {q.data && q.data.tournaments.length === 0 && <EmptyState />}

      {q.data && q.data.tournaments.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-body-sm">
            <thead>
              <tr className="text-ink-300 font-mono uppercase tracking-wider text-[11px]">
                <th className="text-left py-2 pr-3 font-medium">Date</th>
                <th className="text-left py-2 pr-3 font-medium">Tournoi</th>
                <th className="text-left py-2 pr-3 font-medium">Cadence</th>
                <th className="text-right py-2 pr-3 font-medium">Score</th>
                <th className="text-right py-2 pr-3 font-medium">Perf</th>
                <th className="text-right py-2 font-medium">Δ</th>
              </tr>
            </thead>
            <tbody>
              {q.data.tournaments.map((t) => (
                <tr
                  key={t.id}
                  className="border-t border-ink-700 hover:bg-ink-900 transition-colors"
                >
                  <td className="py-2 pr-3 align-top text-paper-50 font-mono tabular-nums">
                    {t.started_at}
                  </td>
                  <td className="py-2 pr-3 align-top">
                    {t.url ? (
                      <a
                        href={t.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-paper-50 hover:text-acid-500 inline-flex items-center gap-1"
                      >
                        <span>{t.name}</span>
                        <ArrowSquareOut
                          size={12}
                          weight="regular"
                          aria-hidden="true"
                        />
                      </a>
                    ) : (
                      <span className="text-paper-50">{t.name}</span>
                    )}
                    {t.location && (
                      <span className="block text-ink-300 text-[11px] italic mt-0.5">
                        {t.location}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 align-top">
                    <span className="inline-block px-1.5 py-0.5 rounded border border-ink-700 text-ink-300 font-mono uppercase tracking-wider text-[10px]">
                      {formatCadence(t.time_control)}
                    </span>
                  </td>
                  <td className="py-2 pr-3 align-top text-right text-paper-50 font-mono tabular-nums">
                    {formatScore(t)}
                  </td>
                  <td className="py-2 pr-3 align-top text-right text-paper-50 font-mono tabular-nums">
                    {t.perf !== null ? t.perf : '—'}
                  </td>
                  <td className="py-2 align-top text-right">
                    <VariationCell value={t.elo_variation} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
